/**
 * AnnouncementBanner — Iter 191x
 *
 * A thin, dismissible top bar rendered above <SiteHeader> on the public
 * storefront. Vendor configures it under Pages › Marketing Tools ›
 * Announcement Banner.  Data shape (persisted at settings.announcement):
 *   {
 *     enabled:         bool
 *     text:            string
 *     link_url:        string
 *     link_text:       string
 *     background:      string   (hex/CSS)
 *     text_color:      string
 *     dismissible:     bool
 *     show_on_mobile:  bool
 *   }
 *
 * Buyers who dismiss it get a per-site sessionStorage flag so it stays
 * hidden for the tab lifetime.
 */
import React, { useState } from 'react';
import { X as XIcon } from 'lucide-react';

export default function AnnouncementBanner({ announcement, subdomain }) {
  const cfg = announcement || {};
  const key = `announcement_dismissed:${subdomain || 'site'}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return typeof window !== 'undefined' && !!sessionStorage.getItem(key); }
    catch { return false; }
  });

  if (!cfg.enabled) return null;
  if (dismissed) return null;
  if (!cfg.text && !cfg.link_text) return null;

  const bg = cfg.background || '#0F0F23';
  const fg = cfg.text_color || '#FFFFFF';

  return (
    <div
      className="w-full text-center py-2.5 px-4 text-sm font-medium relative"
      style={{ background: bg, color: fg }}
      data-testid="site-announcement-banner"
    >
      <span>{cfg.text}</span>
      {cfg.link_url && cfg.link_text && (
        <a
          href={cfg.link_url}
          className="ml-2 underline underline-offset-2 font-semibold hover:opacity-80"
          style={{ color: fg }}
          data-testid="site-announcement-cta"
        >
          {cfg.link_text}
        </a>
      )}
      {cfg.dismissible && (
        <button
          type="button"
          onClick={() => {
            try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
            setDismissed(true);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full hover:bg-black/10 transition-colors"
          aria-label="Dismiss announcement"
          data-testid="site-announcement-dismiss"
          style={{ color: fg }}
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );
}
